/**
 * BrandBadge — the single, shared brand indicator pill (#560).
 *
 * Every screen that shows a contact's brand (Contacts list + detail header,
 * Inbox conversation list + thread-side ContactPanel, Queue Console list /
 * active panel / preview cards) renders this component so the same contact
 * shows the same brand color everywhere.
 *
 * Color rules:
 *  - Each known Globe Life brand maps to a dedicated design token defined in
 *    styles/theme.css (`--brand-liberty`, `--brand-american-income`, …) with
 *    light/dark variants tuned for WCAG AA contrast over the 10%-tint pill
 *    background. No hex literals live here or at any call site.
 *  - Unrecognized brand strings fall back to one of three `--brand-alt-*`
 *    hues picked by a deterministic string hash, so an unknown brand still
 *    gets a stable, consistent color on every screen.
 *  - Missing brand (or the literal "Unknown" produced by the server's
 *    deriveBrand) renders a neutral gray pill so the absence is visible
 *    without shouting.
 *
 * The pill always pairs color with the brand text label — color is never
 * the only signal.
 */

import { cn } from '../ui/utils';

/** Canonical brand → pill classes. Keys are lowercase canonical brand names
 *  as produced by src/utils/brand-derivation.ts. Class strings must be full
 *  literals so Tailwind's scanner picks them up. */
const BRAND_PILL_CLASSES: Record<string, string> = {
  'liberty national': 'bg-brand-liberty/10 text-brand-liberty border-brand-liberty/25',
  'american income':
    'bg-brand-american-income/10 text-brand-american-income border-brand-american-income/25',
  'altig organization': 'bg-brand-altig/10 text-brand-altig border-brand-altig/25',
  'family heritage':
    'bg-brand-family-heritage/10 text-brand-family-heritage border-brand-family-heritage/25',
  nilico: 'bg-brand-nilico/10 text-brand-nilico border-brand-nilico/25',
};

/** Vendor/legacy spellings → canonical key above. Mirrors the alias table in
 *  src/utils/brand-derivation.ts so rows that still carry raw vendor text
 *  color the same as their derived brand. */
const BRAND_ALIASES: Record<string, string> = {
  ail: 'american income',
  'american income life': 'american income',
  lbn: 'liberty national',
  lnl: 'liberty national',
  'national income': 'nilico',
  'national income life': 'nilico',
  'nilico brim': 'nilico',
  arias: 'altig organization',
  'altig orgnanization': 'altig organization', // typo in vendor data
  'family heritage life': 'family heritage',
  'family first': 'family heritage',
};

/** Deterministic fallback palette for brands we don't recognize. */
const HASH_PILL_CLASSES = [
  'bg-brand-alt-1/10 text-brand-alt-1 border-brand-alt-1/25',
  'bg-brand-alt-2/10 text-brand-alt-2 border-brand-alt-2/25',
  'bg-brand-alt-3/10 text-brand-alt-3 border-brand-alt-3/25',
];

/** Neutral pill for contacts with no brand set (or brand "Unknown"). */
const NEUTRAL_PILL_CLASSES = 'bg-secondary text-muted-foreground border-border/60';

/** djb2 — tiny, stable string hash so unknown brands keep the same color
 *  across screens and sessions. */
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return h >>> 0;
}

/** Color classes for a brand value. Exported for the rare surface that needs
 *  brand coloring on something other than the standard pill. */
export function brandPillClasses(brand?: string | null): string {
  const raw = (brand ?? '').trim();
  if (!raw || raw.toLowerCase() === 'unknown') return NEUTRAL_PILL_CLASSES;
  const key = raw.toLowerCase();
  const canonical = BRAND_PILL_CLASSES[key] ? key : BRAND_ALIASES[key];
  if (canonical && BRAND_PILL_CLASSES[canonical]) return BRAND_PILL_CLASSES[canonical];
  return HASH_PILL_CLASSES[hashString(key) % HASH_PILL_CLASSES.length]!;
}

interface BrandBadgeProps {
  brand?: string | null;
  /** `sm` (default) matches the standard 0.625rem badge row pills;
   *  `xs` matches the denser 0.5625rem pills used in compact lists. */
  size?: 'xs' | 'sm';
  className?: string;
}

export function BrandBadge({ brand, size = 'sm', className }: BrandBadgeProps) {
  const label = (brand ?? '').trim() || 'No Brand';
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full font-bold tracking-wide border whitespace-nowrap',
        size === 'xs' ? 'text-[0.5625rem]' : 'text-[0.625rem]',
        brandPillClasses(brand),
        className,
      )}
    >
      {label}
    </span>
  );
}
